import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import procurementService from '../../services/procurement.service';
import { useLanguage } from '../../i18n/LanguageContext';

const SupplierList: React.FC = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    try { const res = await procurementService.getSuppliers(true); setSuppliers(res.data); }
    catch (error) { console.error('Error loading suppliers:', error); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(t('suppliers.delete_confirm'))) return;
    try { await procurementService.deleteSupplier(id); loadSuppliers(); }
    catch (error: any) { alert(error.response?.data?.message || 'Error'); }
  };

  const columns: GridColDef[] = [
    { field: 'code', headerName: t('common.code'), width: 100 },
    { field: 'name', headerName: t('common.name'), flex: 1 },
    { field: 'contactPerson', headerName: t('common.contact_person'), width: 150 },
    { field: 'email', headerName: t('common.email'), width: 180 },
    { field: 'phone', headerName: t('common.phone'), width: 130 },
    { field: 'city', headerName: t('common.city'), width: 120 },
    { field: 'country', headerName: t('common.country'), width: 100 },
    { field: 'paymentTerms', headerName: t('common.payment_terms'), width: 130 },
    {
      field: 'isActive', headerName: t('common.status'), width: 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.value ? t('common.active') : t('common.inactive')} color={params.value ? 'success' : 'default'} size="small" />
      )
    },
    { field: 'orderCount', headerName: t('suppliers.orders'), width: 80, align: 'center' },
    {
      field: 'actions', headerName: t('common.actions'), width: 120, sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title={t('common.edit')}><IconButton size="small" onClick={() => navigate(`/suppliers/edit/${params.row.id}`)}><Edit fontSize="small" /></IconButton></Tooltip>
          <Tooltip title={t('common.delete')}><IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}><Delete fontSize="small" /></IconButton></Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('suppliers.title')}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/suppliers/new')}>{t('suppliers.new')}</Button>
      </Box>
      <DataGrid rows={suppliers} columns={columns} loading={loading} autoHeight
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50]} disableRowSelectionOnClick />
    </Box>
  );
};

export default SupplierList;
